import React, { useState, useMemo } from 'react';
import { useIsMobile } from '../useIsMobile';
import { isAdmin } from './AdminDashboard';
import LedgerTab from './LedgerTab';
import {
  EXPENSE_RATE_TABLE,
  determineFilingMode,
  compareFilingModes,
  calcYouthStartupCredit,
  calcBookkeepingCredit,
} from '../data/taxConstants';
import { calcMonthlyPayroll } from '../utils/payroll';
import { useTransactions } from '../useTransactions';
import { aggregateVatPeriod, getPresumedRate } from '../utils/vatCalc';

// ── 유틸
const fmt = (n) => Math.round(n || 0).toLocaleString('ko-KR');
const fmtWon = (n) => fmt(n) + '원';
const thisYear = new Date().getFullYear();
const thisMonth = new Date().getMonth() + 1;

// 과세기간 (VAT)
const VAT_PERIODS = [
  { label: `${thisYear}년 1기 (1~6월)`, months: Array.from({length:6},(_,i)=>`${thisYear}-${String(i+1).padStart(2,'0')}`), deadline: `${thisYear}-07-25` },
  { label: `${thisYear}년 2기 (7~12월)`, months: Array.from({length:6},(_,i)=>`${thisYear}-${String(i+7).padStart(2,'0')}`), deadline: `${thisYear+1}-01-25` },
  { label: `${thisYear-1}년 2기 (7~12월)`, months: Array.from({length:6},(_,i)=>`${thisYear-1}-${String(i+7).padStart(2,'0')}`), deadline: `${thisYear}-01-25` },
];

// 소득세 구간
const INCOME_TAX_BRACKETS = [
  { limit: 14000000,  rate: 0.06,  deduction: 0 },
  { limit: 50000000,  rate: 0.15,  deduction: 1260000 },
  { limit: 88000000,  rate: 0.24,  deduction: 5760000 },
  { limit: 150000000, rate: 0.35,  deduction: 15440000 },
  { limit: 300000000, rate: 0.38,  deduction: 19940000 },
  { limit: 500000000, rate: 0.40,  deduction: 25940000 },
  { limit: 1000000000,rate: 0.42,  deduction: 35940000 },
  { limit: Infinity,  rate: 0.45,  deduction: 65940000 },
];

function calcIncomeTax(income) {
  if (income <= 0) return 0;
  const bracket = INCOME_TAX_BRACKETS.find(b => income <= b.limit);
  return Math.max(0, income * bracket.rate - bracket.deduction);
}

// 원천세율
const WITHHOLDING_RATE = 0.033; // 3.3% (사업소득 원천징수)
const EMPLOYEE_RATE = 0.094;    // 9.4% (근로소득세 + 지방소득세 + 4대보험 근사치)

// ── 홈택스 가이드 데이터
const HOMETAX_GUIDES = {
  vat: {
    title: '부가가치세 신고',
    url: 'https://www.hometax.go.kr/websquare/websquare.html?w2xPath=/ui/pp/index.html',
    path: '세금신고 → 부가가치세 → 일반과세자 신고',
    steps: [
      { num: '1', title: '홈택스 로그인', desc: '공동인증서(공인인증서) 또는 간편인증으로 로그인', icon: '🔑' },
      { num: '2', title: '신고서 불러오기', desc: '세금신고 > 부가가치세 > 확정신고 > 일반과세자', icon: '📋' },
      { num: '3', title: '매출세액 입력', desc: '과세표준 및 매출세액 > 세금계산서 발급분 입력', icon: '💰' },
      { num: '4', title: '매입세액 공제', desc: '매입세액 > 세금계산서 수취분 > 합계 입력', icon: '🧾' },
      { num: '5', title: '납부세액 확인', desc: '매출세액 - 매입세액 = 납부(환급)세액 자동 계산', icon: '✅' },
      { num: '6', title: '신고서 제출', desc: '신고서 내용 확인 후 신고서 제출 클릭', icon: '📤' },
      { num: '7', title: '납부', desc: '납부기한 내 인터넷뱅킹 또는 납부번호로 납부', icon: '🏦' },
    ],
  },
  income: {
    title: '종합소득세 신고',
    url: 'https://www.hometax.go.kr',
    path: '세금신고 → 종합소득세 → 모두채움/단순경비율 신고',
    steps: [
      { num: '1', title: '홈택스 로그인', desc: '5월 신고기간 (5/1~5/31)', icon: '🔑' },
      { num: '2', title: '신고 유형 확인', desc: '세금신고 > 종합소득세 > 신고 유형 선택 (모두채움/일반)', icon: '📋' },
      { num: '3', title: '사업소득 입력', desc: '수입금액 및 필요경비 입력 (셈핏 손익관리 데이터 활용)', icon: '💼' },
      { num: '4', title: '공제 항목 확인', desc: '인적공제, 노란우산공제, 4대보험 등 공제 입력', icon: '💳' },
      { num: '5', title: '세액 계산', desc: '과세표준 × 세율 - 세액공제 = 납부세액', icon: '🧮' },
      { num: '6', title: '신고서 제출', desc: '신고서 확인 후 제출. 분납 가능 (2개월 이내)', icon: '📤' },
    ],
  },
  withholding: {
    title: '원천세 신고',
    url: 'https://www.hometax.go.kr',
    path: '세금신고 → 원천세 → 원천세 신고',
    steps: [
      { num: '1', title: '매월 신고', desc: '급여 지급한 다음달 10일까지 신고·납부', icon: '📅' },
      { num: '2', title: '홈택스 접속', desc: '세금신고 > 원천세 > 원천세(매월) 신고', icon: '💻' },
      { num: '3', title: '지급명세서 작성', desc: '근로자별 급여, 원천징수세액 입력', icon: '📝' },
      { num: '4', title: '세액 납부', desc: '신고 후 납부번호로 납부 (소득세 + 지방소득세)', icon: '🏦' },
      { num: '5', title: '연말정산', desc: '다음해 2월, 연말정산 지급명세서 제출', icon: '📊' },
    ],
  },
};

// ── 공통 컴포넌트
function Card({ children, style }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '14px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
      marginBottom: '14px', overflow: 'hidden', ...style,
    }}>{children}</div>
  );
}

function CardHeader({ icon, title, color = '#FF6F0F', badge }) {
  return (
    <div style={{
      padding: '13px 18px', borderBottom: '1px solid #f0f0f0',
      display: 'flex', alignItems: 'center', gap: '8px',
    }}>
      <span style={{ fontWeight: '800', fontSize: '15px', color, flex: 1 }}>{title}</span>
      {badge && <span style={{
        fontSize: '11px', background: badge.bg, color: badge.color,
        padding: '2px 10px', borderRadius: '10px', fontWeight: '700',
      }}>{badge.text}</span>}
    </div>
  );
}

function InfoRow({ label, value, valueColor, bold }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 0', borderBottom: '1px solid #f5f5f5',
    }}>
      <span style={{ fontSize: '13px', color: '#6b7280' }}>{label}</span>
      <span style={{
        fontSize: bold ? '16px' : '14px',
        fontWeight: bold ? '900' : '700',
        color: valueColor || '#1a1a1a',
      }}>{value}</span>
    </div>
  );
}

function HometaxButton({ guide }) {
  return (
    <a href={guide.url} target="_blank" rel="noopener noreferrer"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '7px',
        padding: '11px 20px', borderRadius: '10px', textDecoration: 'none',
        background: '#2563EB',
        color: '#fff', fontWeight: '800', fontSize: '14px',
        boxShadow: '0 3px 12px rgba(37,99,235,0.35)',
      }}>
      🏛️ 홈택스 바로가기
      <span style={{ fontSize: '11px', opacity: 0.8, fontWeight: '500' }}>↗</span>
    </a>
  );
}

function StepGuide({ guide }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: '14px' }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', padding: '10px 14px', borderRadius: '10px',
        border: '1.5px solid #e5e7eb', background: '#f9fafb',
        display: 'flex', alignItems: 'center', gap: '8px',
        cursor: 'pointer', fontWeight: '700', fontSize: '13px', color: '#374151',
      }}>
        <span>📖</span>
        <span style={{ flex: 1, textAlign: 'left' }}>홈택스 {guide.title} 단계별 가이드</span>
        <span>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{
            background: '#EFF6FF', borderRadius: '8px', padding: '10px 14px',
            fontSize: '12px', color: '#1D4ED8', fontWeight: '600',
          }}>
            📍 경로: {guide.path}
          </div>
          {guide.steps.map(s => (
            <div key={s.num} style={{
              display: 'flex', gap: '12px', padding: '12px 14px',
              background: '#f9fafb', borderRadius: '10px', alignItems: 'flex-start',
            }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: '#FF6F0F', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: '800', flexShrink: 0,
              }}>{s.num}</div>
              <div>
                <div style={{ fontWeight: '800', fontSize: '13px', color: '#1a1a1a', marginBottom: '2px' }}>
                  {s.icon} {s.title}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 탭: 신고 일정
function DeadlineTab() {
  const year = thisYear;
  const deadlines = [
    { month: 1,  day: 10, type: '원천세', desc: `${year-1}년 12월분 급여 원천세 신고·납부`, color: '#7C3AED', urgent: thisMonth === 1 },
    { month: 1,  day: 25, type: '부가세', desc: `${year-1}년 2기 확정 부가가치세 신고·납부`, color: '#1A56A0', urgent: thisMonth === 1 },
    { month: 2,  day: 10, type: '원천세', desc: `${year}년 1월분 급여 원천세 신고·납부`, color: '#7C3AED' },
    { month: 2,  day: 28, type: '연말정산', desc: '근로소득 지급명세서 제출', color: '#EA580C' },
    { month: 3,  day: 10, type: '원천세', desc: `${year}년 2월분 급여 원천세 신고·납부`, color: '#7C3AED' },
    { month: 4,  day: 10, type: '원천세', desc: `${year}년 3월분 급여 원천세 신고·납부`, color: '#7C3AED' },
    { month: 4,  day: 25, type: '부가세', desc: `${year}년 1기 예정 부가가치세 신고·납부`, color: '#1A56A0', urgent: thisMonth === 4 },
    { month: 5,  day: 10, type: '원천세', desc: `${year}년 4월분 급여 원천세 신고·납부`, color: '#7C3AED' },
    { month: 5,  day: 31, type: '종합소득세', desc: `${year-1}년 귀속 종합소득세 신고·납부`, color: '#EA580C', urgent: thisMonth === 5, important: true },
    { month: 6,  day: 10, type: '원천세', desc: `${year}년 5월분 급여 원천세 신고·납부`, color: '#7C3AED' },
    { month: 7,  day: 10, type: '원천세', desc: `${year}년 6월분 급여 원천세 신고·납부`, color: '#7C3AED' },
    { month: 7,  day: 25, type: '부가세', desc: `${year}년 1기 확정 부가가치세 신고·납부`, color: '#1A56A0', urgent: thisMonth === 7 },
    { month: 10, day: 25, type: '부가세', desc: `${year}년 2기 예정 부가가치세 신고·납부`, color: '#1A56A0', urgent: thisMonth === 10 },
  ];

  const upcoming = deadlines.filter(d => d.month >= thisMonth).slice(0, 5);

  return (
    <div>
      <Card>
        <CardHeader icon="📅" title="다가오는 신고 일정" color="#FF6F0F" />
        <div style={{ padding: '16px 18px' }}>
          {upcoming.map((d, i) => {
            const dday = Math.ceil((new Date(year, d.month - 1, d.day) - new Date()) / 86400000);
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '12px 0', borderBottom: i < upcoming.length - 1 ? '1px solid #f0f0f0' : 'none',
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '10px',
                  background: d.color + '15', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <span style={{ fontSize: '11px', color: d.color, fontWeight: '700' }}>{d.month}월</span>
                  <span style={{ fontSize: '18px', fontWeight: '900', color: d.color, lineHeight: 1 }}>{d.day}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                    <span style={{
                      fontSize: '11px', background: d.color + '20', color: d.color,
                      padding: '1px 8px', borderRadius: '8px', fontWeight: '700',
                    }}>{d.type}</span>
                    {d.important && <span style={{ fontSize: '10px', background: '#F4F4F5', color: '#52525B', padding: '1px 6px', borderRadius: '6px', fontWeight: '700' }}>중요</span>}
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>{d.desc}</div>
                </div>
                <div style={{
                  fontSize: '12px', fontWeight: '800', color: dday <= 7 ? '#52525B' : dday <= 30 ? '#EA580C' : '#9ca3af',
                  flexShrink: 0,
                }}>
                  {dday <= 0 ? '오늘!' : `D-${dday}`}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <CardHeader icon="💡" title="신고 전 체크리스트" color="#EA580C" />
        <div style={{ padding: '16px 18px' }}>
          {[
            { icon: '🧾', text: '세금계산서 발행/수취 내역 확인', link: '세금계산서 탭' },
            { icon: '💰', text: '월별 매출·매입 금액 손익관리에 입력', link: '손익관리 탭' },
            { icon: '👥', text: '직원 급여 및 원천징수 내역 정리', link: '원천세 탭' },
            { icon: '🏛️', text: '공인인증서/간편인증 준비', link: null },
            { icon: '🏦', text: '납부 계좌 잔액 확인', link: null },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '9px 0', borderBottom: i < 4 ? '1px solid #f5f5f5' : 'none',
            }}>
              <span style={{ fontSize: '16px' }}>{item.icon}</span>
              <span style={{ fontSize: '13px', color: '#374151', flex: 1 }}>{item.text}</span>
              {item.link && (
                <span style={{ fontSize: '11px', color: '#18181B', fontWeight: '700' }}>→ {item.link}</span>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── 탭: 부가가치세
function VatTab({ profitLoss, taxData, onTaxDataChange, user }) {
  const [periodIdx, setPeriodIdx] = useState(thisMonth <= 6 ? 0 : 1);
  const [bizType, setBizType] = useState(taxData?.bizType || 'general');
  const period = VAT_PERIODS[periodIdx];

  // V17: 거래 데이터 자동 집계 — 의제매입공제 포함
  const periodStart = period.months[0] + '-01';
  const periodEnd = period.months[5] + '-31';
  const { transactions: periodTxns } = useTransactions(user, { startDate: periodStart, endDate: periodEnd, limitCount: 5000 });
  const vatAgg = useMemo(() => aggregateVatPeriod(periodTxns || [], period.months, bizType), [periodTxns, period.months, bizType]);

  // 기간 내 매출 자동 집계
  const autoRevenue = useMemo(() => {
    if (!profitLoss) return 0;
    return period.months.reduce((sum, m) => {
      const r = profitLoss[m]?.revenue;
      return sum + (r ? (r.card || 0) + (r.cash || 0) + (r.delivery || 0) : 0);
    }, 0);
  }, [profitLoss, period]);

  // 기간 내 매입 자동 집계
  const autoCosts = useMemo(() => {
    if (!profitLoss) return 0;
    return period.months.reduce((sum, m) => {
      const c = profitLoss[m]?.costs;
      return sum + (c ? Object.values(c).reduce((s, v) => s + (v || 0), 0) : 0);
    }, 0);
  }, [profitLoss, period]);

  const [revenue, setRevenue] = useState(0);
  const [purchase, setPurchase] = useState(0);
  const [revenueAdjusted, setRevenueAdjusted] = useState(false);

  const salesTax = Math.round(revenue * 0.1);
  const purchaseTax = Math.round(purchase * 0.1);
  const payTax = salesTax - purchaseTax;

  const applyAutoData = () => {
    setRevenue(autoRevenue);
    setPurchase(autoCosts);
    setRevenueAdjusted(true);
  };

  return (
    <div>
      {/* 사업자 유형 */}
      <Card>
        <CardHeader icon="🏪" title="사업자 유형" color="#1A56A0" />
        <div style={{ padding: '16px 18px', display: 'flex', gap: '10px' }}>
          {[
            { id: 'general', label: '일반과세자', desc: '연매출 8,000만원 이상' },
            { id: 'simplified', label: '간이과세자', desc: '연매출 8,000만원 미만' },
          ].map(t => (
            <button key={t.id} onClick={() => { setBizType(t.id); onTaxDataChange({...taxData, bizType: t.id}); }}
              style={{
                flex: 1, padding: '12px', borderRadius: '10px', cursor: 'pointer',
                border: bizType === t.id ? '2px solid #1A56A0' : '2px solid #e5e7eb',
                background: bizType === t.id ? '#EFF6FF' : '#fff', textAlign: 'center',
              }}>
              <div style={{ fontWeight: '800', fontSize: '14px', color: bizType === t.id ? '#1A56A0' : '#374151' }}>{t.label}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{t.desc}</div>
            </button>
          ))}
        </div>
        {bizType === 'simplified' && (
          <div style={{ margin: '0 18px 16px', background: '#F4F4F5', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#52525B' }}>
            ⚠️ 간이과세자는 업종별 부가가치율(15~40%)을 적용합니다. 매입세액 공제가 제한될 수 있어요.
          </div>
        )}
      </Card>

      {/* 과세기간 선택 */}
      <Card>
        <CardHeader icon="📅" title="과세기간 선택" color="#1A56A0" />
        <div style={{ padding: '14px 18px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {VAT_PERIODS.map((p, i) => (
            <button key={i} onClick={() => setPeriodIdx(i)}
              style={{
                padding: '8px 16px', borderRadius: '20px', cursor: 'pointer',
                border: periodIdx === i ? '2px solid #1A56A0' : '2px solid #e5e7eb',
                background: periodIdx === i ? '#1A56A0' : '#fff',
                color: periodIdx === i ? '#fff' : '#555',
                fontSize: '13px', fontWeight: '700',
              }}>{p.label}</button>
          ))}
        </div>
        <div style={{ margin: '0 18px 16px', background: '#EFF6FF', borderRadius: '8px', padding: '10px 14px' }}>
          <div style={{ fontSize: '12px', color: '#1D4ED8', fontWeight: '600' }}>
            📌 신고 기한: {period.deadline} | 대상기간: {period.months[0]}월 ~ {period.months[5]}월
          </div>
        </div>
      </Card>

      {/* V17: 거래 기반 자동 집계 (의제매입공제 포함) */}
      {(vatAgg.salesVat > 0 || vatAgg.purchaseVat > 0 || vatAgg.presumedConfirmed > 0) && (
        <Card>
          <CardHeader icon="🤖" title="거래 데이터로 자동 계산" color="#1A56A0"
            badge={{ text: '실시간', bg: '#D6EDE0', color: '#18181B' }} />
          <div style={{ padding: '14px 18px' }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
              📅 {period.months[0]} ~ {period.months[5]} · 거래 {(periodTxns || []).length}건 분석
            </div>

            {/* 매출 */}
            <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9a3412', marginBottom: 4 }}>💰 매출세액</div>
              <InfoRow label="과세 매출 공급가액" value={fmtWon(vatAgg.salesSupply)} />
              <InfoRow label="매출세액 (10%)" value={fmtWon(vatAgg.salesVat)} bold />
              {vatAgg.salesTaxFree > 0 && (
                <InfoRow label="면세 매출" value={fmtWon(vatAgg.salesTaxFree)} valueColor="#854d0e" />
              )}
            </div>

            {/* 매입 */}
            <div style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 4 }}>🧾 매입세액 공제</div>
              <InfoRow label="적격증빙 매입 공급가액" value={fmtWon(vatAgg.purchaseSupply)} />
              <InfoRow label="매입세액 (공제 가능)" value={fmtWon(vatAgg.purchaseVat)} bold />
              {vatAgg.purchaseNonQualified > 0 && (
                <InfoRow label="비적격증빙 매입 (공제 X)" value={fmtWon(vatAgg.purchaseNonQualified)} valueColor="#52525B" />
              )}
            </div>

            {/* 의제매입공제 */}
            <div style={{ background: vatAgg.presumedConfirmed > 0 ? '#FAFAFA' : '#f9fafb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, border: vatAgg.presumedPendingCount > 0 ? '1px dashed #A1A1AA' : 'none' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9A3412', marginBottom: 4 }}>🎁 의제매입공제 ({Math.round(vatAgg.presumedRate * 1000) / 10}%)</div>
              {vatAgg.presumedConfirmed > 0 ? (
                <>
                  <InfoRow label="확정 면세 매입 합계" value={fmtWon(vatAgg.presumedConfirmed)} />
                  {vatAgg.presumedLimitExceeded ? (
                    <>
                      <InfoRow label="원래 공제액" value={fmtWon(vatAgg.presumedDeductionRaw)} valueColor="#9ca3af" />
                      <InfoRow label={`한도 (매출 × ${Math.round(vatAgg.limitRate * 100)}%)`} value={fmtWon(vatAgg.presumedLimit)} valueColor="#52525B" />
                      <InfoRow label="✂ 한도 적용 공제 세액" value={fmtWon(vatAgg.presumedDeduction)} bold valueColor="#9A3412" />
                    </>
                  ) : (
                    <InfoRow label="공제 세액" value={fmtWon(vatAgg.presumedDeduction)} bold valueColor="#9A3412" />
                  )}
                </>
              ) : (
                <div style={{ fontSize: 11, color: '#6b7280' }}>확정된 후보 없음. 영수증 업로드 시 1차 농수축산물에 "🎁 의제매입공제 후보" 체크 필요.</div>
              )}
              {vatAgg.presumedLimitExceeded && (
                <div style={{ fontSize: 11, color: '#52525B', marginTop: 6, padding: '6px 8px', background: '#F4F4F5', borderRadius: 5 }}>
                  ⚠ 한도 초과 — 확정 면세 매입이 매출 한도를 넘어 {fmtWon(vatAgg.presumedDeductionRaw - vatAgg.presumedDeduction)}원 공제 불가
                </div>
              )}
              {vatAgg.presumedPendingCount > 0 && (
                <div style={{ fontSize: 11, color: '#52525B', marginTop: 6, padding: '6px 8px', background: '#F4F4F5', borderRadius: 5 }}>
                  ⚠ AI 제안 + 사용자 미확정 거래 {vatAgg.presumedPendingCount}건 ({fmtWon(vatAgg.presumedPendingAmount)}원). 거래장부에서 검토 후 의제매입공제 후보 확정 권장.
                </div>
              )}
            </div>

            {/* 납부세액 */}
            <div style={{ background: vatAgg.payable >= 0 ? '#fef2f2' : '#eff6ff', borderRadius: 10, padding: '12px 14px', marginTop: 10 }}>
              <InfoRow label="매출세액" value={fmtWon(vatAgg.salesVat)} />
              <InfoRow label="(-) 매입세액" value={`-${fmtWon(vatAgg.purchaseVat)}`} />
              {vatAgg.presumedDeduction > 0 && (
                <InfoRow label="(-) 의제매입공제" value={`-${fmtWon(vatAgg.presumedDeduction)}`} valueColor="#9A3412" />
              )}
              <div style={{ height: 1, background: '#e5e7eb', margin: '8px 0' }} />
              <InfoRow
                label={vatAgg.payable >= 0 ? '🔴 납부 예상세액' : '🔵 환급 예상세액'}
                value={fmtWon(Math.abs(vatAgg.payable))}
                valueColor={vatAgg.payable >= 0 ? '#52525B' : '#1A56A0'}
                bold
              />
            </div>

            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8, lineHeight: 1.5 }}>
              ※ 거래장부 데이터 기반 예상치. 의제매입공제는 매출 × {Math.round(vatAgg.limitRate * 100)}% 한도까지 자동 캡 (V19). 매출 4억 초과 시 한도율이 점진적으로 줄어드니 실제 신고 시 확인 필요.
            </div>
          </div>
        </Card>
      )}

      {/* 매출/매입 입력 */}
      <Card>
        <CardHeader icon="💰" title="매출·매입 세액 계산 (수동)" color="#1A56A0"
          badge={{ text: '자동연동 가능', bg: '#D6EDE0', color: '#18181B' }} />
        <div style={{ padding: '16px 18px' }}>
          {autoRevenue > 0 && !revenueAdjusted && (
            <div style={{
              background: '#FAFAFA', borderRadius: '10px', padding: '12px 14px',
              marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: '#18181B', fontWeight: '700' }}>📊 손익관리에서 자동 집계</div>
                <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>
                  매출 {fmtWon(autoRevenue)} · 비용 {fmtWon(autoCosts)}
                </div>
              </div>
              <button onClick={applyAutoData} style={{
                padding: '8px 14px', borderRadius: '8px', border: 'none',
                background: '#FF6F0F', color: '#fff', fontSize: '12px',
                fontWeight: '800', cursor: 'pointer', flexShrink: 0,
              }}>자동 적용</button>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '13px', color: '#555', display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                💰 과세 매출액 (부가세 제외 금액)
              </label>
              <input type="number" value={revenue || ''} onChange={e => setRevenue(Number(e.target.value)||0)}
                placeholder="0"
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', textAlign: 'right' }} />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>매출세액: {fmtWon(salesTax)} (10%)</div>
            </div>
            <div>
              <label style={{ fontSize: '13px', color: '#555', display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                🧾 매입액 (세금계산서 수취 금액, 부가세 제외)
              </label>
              <input type="number" value={purchase || ''} onChange={e => setPurchase(Number(e.target.value)||0)}
                placeholder="0"
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', textAlign: 'right' }} />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>매입세액: {fmtWon(purchaseTax)} (공제 가능)</div>
            </div>
          </div>

          <div style={{ marginTop: '16px', background: payTax >= 0 ? '#FAFAFA' : '#EFF6FF', borderRadius: '12px', padding: '14px 16px' }}>
            <InfoRow label="매출세액" value={fmtWon(salesTax)} />
            <InfoRow label="(-) 매입세액 공제" value={`-${fmtWon(purchaseTax)}`} />
            <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 0' }} />
            <InfoRow
              label={payTax >= 0 ? '🔴 납부해야 할 세액' : '🔵 환급받을 세액'}
              value={fmtWon(Math.abs(payTax))}
              valueColor={payTax >= 0 ? '#52525B' : '#1A56A0'}
              bold
            />
          </div>

          <div style={{ marginTop: '14px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <HometaxButton guide={HOMETAX_GUIDES.vat} />
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>→ {HOMETAX_GUIDES.vat.path}</div>
          </div>
          <StepGuide guide={HOMETAX_GUIDES.vat} />
        </div>
      </Card>
    </div>
  );
}

// ── 탭: 종합소득세 — 단순/기준경비율/간편장부 3가지 모드 비교
function IncomeTaxTab({ profitLoss }) {
  const [year, setYear] = useState(thisYear - 1);
  const [industryCode, setIndustryCode] = useState('552101'); // 한식 일반음식점 default
  const [isNew, setIsNew] = useState(true);                    // 신규사업자 여부
  const [prevRevenueInput, setPrevRevenueInput] = useState(0); // 직전 수입 (기존사업자만)

  // 매출 / 주요경비
  const [revenue, setRevenue] = useState(0);
  const [mainCosts, setMainCosts] = useState({ purchase: 0, rent: 0, labor: 0 });
  const [otherExpense, setOtherExpense] = useState(0);   // 간편장부용 기타 경비

  // 공제·감면
  const [deductions, setDeductions] = useState({
    personal: 1500000,
    pension: 0,
    insurance: 0,
    other: 0,
  });
  const [youthCredit, setYouthCredit] = useState({
    enabled: false,
    region: 'non_capital',
    startedYear: thisYear - 1,
  });
  const [bookkeepingCredit, setBookkeepingCredit] = useState(false); // 복식부기 시 기장세액공제

  // 손익관리에서 자동 집계 (있으면 자동 채우기 도우미)
  // 비용을 카테고리별로 분리: 매입/임차/인건비 = 주요경비 (기준경비율 적용),
  // 수도광열·통신·소모품·기타 = 기타경비 (간편장부에서만 추가 인정)
  const autoData = useMemo(() => {
    if (!profitLoss) return null;
    let rev = 0;
    const mainBreakdown = { purchase: 0, rent: 0, labor: 0 };
    let otherCost = 0;
    Array.from({length:12}, (_,i) => `${year}-${String(i+1).padStart(2,'0')}`).forEach(m => {
      const r = profitLoss[m]?.revenue;
      const c = profitLoss[m]?.costs;
      rev += r ? (r.card||0)+(r.cash||0)+(r.delivery||0) : 0;
      if (c) {
        mainBreakdown.purchase += Number(c.ingredients) || 0;
        mainBreakdown.rent     += Number(c.rent) || 0;
        mainBreakdown.labor    += Number(c.labor) || 0;
        otherCost              += (Number(c.utilities) || 0) + (Number(c.other) || 0);
      }
    });
    const totalMain = mainBreakdown.purchase + mainBreakdown.rent + mainBreakdown.labor;
    return { rev, cost: totalMain + otherCost, mainBreakdown, otherCost };
  }, [profitLoss, year]);

  const applyAuto = () => {
    if (!autoData) return;
    setRevenue(autoData.rev);
    setMainCosts({
      purchase: autoData.mainBreakdown.purchase,
      rent:     autoData.mainBreakdown.rent,
      labor:    autoData.mainBreakdown.labor,
    });
    setOtherExpense(autoData.otherCost);
  };

  // 신고 모드 자동 판정
  const filingMode = useMemo(() => determineFilingMode({
    industryCode,
    prevRevenue: isNew ? null : prevRevenueInput,
    currRevenue: revenue,
  }), [industryCode, isNew, prevRevenueInput, revenue]);

  // 간편장부용 총 경비 = 주요경비 + 기타경비
  const totalExpense = mainCosts.purchase + mainCosts.rent + mainCosts.labor + otherExpense;

  // 3가지 모드 동시 계산
  const comparison = useMemo(() => compareFilingModes({
    industryCode,
    revenue,
    mainCosts,
    totalExpense,
    isDoubleBookRequired: filingMode.required === 'doubleBook',
  }), [industryCode, revenue, mainCosts, totalExpense, filingMode]);

  // 사용자가 선택한 모드
  const [pickedMode, setPickedMode] = useState('auto');
  const finalMode = pickedMode === 'auto' ? comparison.recommendation.mode : pickedMode;
  const finalIncome = (
    finalMode === 'simple' ? comparison.simple.income :
    finalMode === 'standard' ? comparison.standard.income :
    comparison.simpleBook.income
  );

  const totalDeductions = Object.values(deductions).reduce((s, v) => s + (v || 0), 0);
  const taxableIncome = Math.max(0, finalIncome - totalDeductions);
  const baseTax = Math.round(calcIncomeTax(taxableIncome));

  // 청년창업감면 적용
  const youthApplied = youthCredit.enabled
    ? calcYouthStartupCredit({
        baseTax,
        region: youthCredit.region,
        startedYear: youthCredit.startedYear,
      })
    : { credit: 0, rate: 0 };

  // 기장세액공제 (간편장부 대상자가 복식부기로 신고 시)
  const bkCredit = bookkeepingCredit && finalMode === 'simpleBook'
    ? calcBookkeepingCredit({
        baseTax: baseTax - youthApplied.credit,
        businessIncome: finalIncome,
        totalIncome: finalIncome,
      })
    : { credit: 0 };

  const incomeTaxAfterCredits = Math.max(0, baseTax - youthApplied.credit - bkCredit.credit);
  const localTax = Math.round(incomeTaxAfterCredits * 0.1);
  const totalTax = incomeTaxAfterCredits + localTax;
  const bracket = INCOME_TAX_BRACKETS.find(b => taxableIncome <= b.limit);

  const modeAvailable = (m) => filingMode.availableModes.includes(m);

  return (
    <div>
      {/* 1. 업종 + 사업자 상태 */}
      <Card>
        <CardHeader icon="🏪" title="사업 정보" color="#EA580C" />
        <div style={{ padding: '16px 18px' }}>
          {/* 귀속연도 */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {[thisYear, thisYear-1, thisYear-2].map(y => {
              const isCurrent = y === thisYear;
              return (
                <button key={y} onClick={() => setYear(y)}
                  style={{
                    padding: '7px 18px', borderRadius: '20px', cursor: 'pointer',
                    border: year === y
                      ? `2px solid ${isCurrent ? '#7C3AED' : '#EA580C'}`
                      : '2px solid #e5e7eb',
                    background: year === y
                      ? (isCurrent ? '#7C3AED' : '#EA580C')
                      : '#fff',
                    color: year === y ? '#fff' : '#555',
                    fontSize: '13px', fontWeight: '700',
                  }}>{y}년 귀속{isCurrent ? ' (예상)' : ''}</button>
              );
            })}
          </div>

          {/* 진행 중 연도 안내 */}
          {year === thisYear && (
            <div style={{
              padding: '10px 12px', borderRadius: 8, marginBottom: 14,
              background: '#faf5ff', border: '1.5px solid #c4b5fd',
              fontSize: 11, color: '#6b21a8', lineHeight: 1.6,
            }}>
              ⚠️ <strong>진행 중인 연도</strong> — 실제 신고는 {thisYear + 1}년 5월입니다.
              현재까지 입력된 매출·비용 데이터로 예상 종소세를 추정합니다.
              연말까지 데이터가 누적될수록 정확도가 올라가요.
            </div>
          )}

          {/* 업종 */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '13px', color: '#555', fontWeight: '700', display: 'block', marginBottom: '6px' }}>업종 (코드)</label>
            <select value={industryCode} onChange={e => setIndustryCode(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
              {Object.entries(EXPENSE_RATE_TABLE).map(([code, m]) => (
                <option key={code} value={code}>{code} · {m.label} (단순 {(m.simpleRate*100).toFixed(1)}% / 기준 {(m.standardRate*100).toFixed(1)}%)</option>
              ))}
            </select>
          </div>

          {/* 신규/기존 */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            <button onClick={() => setIsNew(true)}
              style={{
                flex: 1, padding: '10px', borderRadius: '10px', cursor: 'pointer',
                border: isNew ? '2px solid #EA580C' : '2px solid #e5e7eb',
                background: isNew ? '#FFFBEB' : '#fff', fontWeight: '700', fontSize: '13px',
                color: isNew ? '#EA580C' : '#555',
              }}>🆕 신규사업자 (작년 첫 개업)</button>
            <button onClick={() => setIsNew(false)}
              style={{
                flex: 1, padding: '10px', borderRadius: '10px', cursor: 'pointer',
                border: !isNew ? '2px solid #EA580C' : '2px solid #e5e7eb',
                background: !isNew ? '#FFFBEB' : '#fff', fontWeight: '700', fontSize: '13px',
                color: !isNew ? '#EA580C' : '#555',
              }}>📊 기존사업자 (직전연도 매출 있음)</button>
          </div>

          {!isNew && (
            <div>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>직전 과세기간 수입금액</label>
              <input type="number" value={prevRevenueInput || ''} onChange={e => setPrevRevenueInput(Number(e.target.value)||0)}
                placeholder="예: 50000000" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', textAlign: 'right' }} />
            </div>
          )}

          {/* 신고 가능 모드 안내 */}
          <div style={{ marginTop: '12px', background: '#EFF6FF', borderRadius: '10px', padding: '10px 14px' }}>
            <div style={{ fontSize: '12px', color: '#1D4ED8', fontWeight: '700' }}>
              📋 신고 가능 모드: {filingMode.availableModes.map(m => ({simple:'단순경비율',standard:'기준경비율',simpleBook:'간편장부',doubleBook:'복식부기'}[m])).join(' · ')}
            </div>
            {filingMode.required === 'doubleBook' && (
              <div style={{ fontSize: '11px', color: '#52525B', marginTop: '4px', fontWeight: '700' }}>⚠️ 복식부기 의무 — 추계신고하면 가산세 발생</div>
            )}
            {filingMode.reasons.simpleAvailable && (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>{filingMode.reasons.simpleAvailable}</div>
            )}
          </div>
        </div>
      </Card>

      {/* 2. 매출 + 주요경비 입력 */}
      <Card>
        <CardHeader icon="💰" title="매출과 경비" color="#EA580C"
          badge={{ text: '자동연동', bg: '#D6EDE0', color: '#18181B' }} />
        <div style={{ padding: '16px 18px' }}>
          {autoData && (autoData.rev > 0 || autoData.cost > 0) && (
            <div style={{
              background: '#FAFAFA', borderRadius: '10px', padding: '12px 14px',
              marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: '#18181B', fontWeight: '700' }}>📊 {year}년 손익관리 자동 집계</div>
                <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>매출 {fmtWon(autoData.rev)} · 비용 {fmtWon(autoData.cost)}</div>
              </div>
              <button onClick={applyAuto} style={{
                padding: '8px 14px', borderRadius: '8px', border: 'none',
                background: '#FF6F0F', color: '#fff', fontSize: '12px', fontWeight: '800', cursor: 'pointer',
              }}>자동 적용</button>
            </div>
          )}

          {/* 매출 */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '13px', color: '#555', fontWeight: '700', display: 'block', marginBottom: '5px' }}>
              💵 연간 수입금액 (매출)
            </label>
            <input type="number" value={revenue || ''} onChange={e => setRevenue(Number(e.target.value)||0)}
              placeholder="예: 60000000" style={{ width: '100%', padding: '11px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '15px', textAlign: 'right', fontWeight: '700' }} />
          </div>

          {/* 주요경비 (기준경비율·간편장부에 영향) */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', color: '#555', fontWeight: '700', marginBottom: '8px' }}>
              📂 주요 경비 <span style={{ fontWeight: '500', fontSize: '11px', color: '#9ca3af' }}>(기준경비율·간편장부에 적용)</span>
            </div>
            {[
              { key: 'purchase', label: '매입비용 (식자재 등)', hint: '세금계산서·카드·현금영수증' },
              { key: 'rent',     label: '임차료',               hint: '월세 합계' },
              { key: 'labor',    label: '인건비',               hint: '직원 급여 + 사업자부담 4대보험' },
            ].map(item => (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '7px' }}>
                <span style={{ fontSize: '13px', color: '#555', width: '160px', flexShrink: 0 }}>{item.label}</span>
                <input type="number" value={mainCosts[item.key] || ''} placeholder="0"
                  onChange={e => setMainCosts({ ...mainCosts, [item.key]: Number(e.target.value)||0 })}
                  style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', textAlign: 'right' }} />
                <span style={{ fontSize: '11px', color: '#9ca3af', width: '160px', flexShrink: 0 }}>{item.hint}</span>
              </div>
            ))}
          </div>

          {/* 기타 경비 (간편장부 전용) */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '13px', color: '#555', width: '160px', flexShrink: 0, fontWeight: '700' }}>📋 기타 경비 합계</span>
              <input type="number" value={otherExpense || ''} placeholder="0"
                onChange={e => setOtherExpense(Number(e.target.value)||0)}
                style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', textAlign: 'right' }} />
              <span style={{ fontSize: '11px', color: '#9ca3af', width: '160px', flexShrink: 0 }}>수도광열·통신·소모품·광고 등</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', textAlign: 'right' }}>
              간편장부 총 경비: {fmtWon(totalExpense)}
            </div>
          </div>
        </div>
      </Card>

      {/* 3. 3가지 모드 비교 */}
      <Card>
        <CardHeader icon="🎯" title="신고 모드 비교 (소득금액)" color="#FF6F0F"
          badge={{ text: '자동 추천', bg: '#F4F4F5', color: '#52525B' }} />
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '14px' }}>
            {[
              {
                mode: 'simple',
                label: '단순경비율',
                desc: '추계 · 영수증 X',
                income: comparison.simple.income,
                expense: comparison.simple.expense,
                detail: `매출 × ${((1 - (EXPENSE_RATE_TABLE[industryCode]?.simpleRate||0))*100).toFixed(1)}%`,
              },
              {
                mode: 'standard',
                label: '기준경비율',
                desc: '추계 · 주요경비만',
                income: comparison.standard.income,
                expense: comparison.standard.expense,
                detail: `주요경비 + 매출 × ${((EXPENSE_RATE_TABLE[industryCode]?.standardRate||0)*100).toFixed(1)}%`,
              },
              {
                mode: 'simpleBook',
                label: '간편장부',
                desc: '기장 · 모든 영수증',
                income: comparison.simpleBook.income,
                expense: comparison.simpleBook.expense,
                detail: `매출 - 실제 경비`,
              },
            ].map(m => {
              const isWinner = comparison.recommendation.mode === m.mode;
              const available = modeAvailable(m.mode);
              const isPicked = finalMode === m.mode;
              return (
                <button key={m.mode} onClick={() => setPickedMode(m.mode)}
                  disabled={!available}
                  style={{
                    padding: '14px 12px', borderRadius: '12px', cursor: available ? 'pointer' : 'not-allowed',
                    textAlign: 'left',
                    border: isPicked ? '2.5px solid #FF6F0F' : (isWinner && available ? '2px solid #FF6F0F' : '2px solid #e5e7eb'),
                    background: !available ? '#f9fafb' : (isPicked ? '#FAFAFA' : (isWinner ? '#FAFAFA' : '#fff')),
                    opacity: available ? 1 : 0.5,
                    position: 'relative',
                  }}>
                  {isWinner && available && (
                    <span style={{
                      position: 'absolute', top: '-8px', right: '8px',
                      fontSize: '10px', background: '#FF6F0F', color: '#fff',
                      padding: '2px 8px', borderRadius: '8px', fontWeight: '800',
                    }}>👑 절세 best</span>
                  )}
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#1a1a1a' }}>{m.label}</div>
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '8px' }}>{m.desc}</div>
                  <div style={{ fontSize: '15px', fontWeight: '900', color: isWinner ? '#FF6F0F' : '#374151' }}>
                    {fmtWon(m.income)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>경비 {fmtWon(m.expense)}</div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '6px', lineHeight: 1.4 }}>{m.detail}</div>
                  {!available && (
                    <div style={{ fontSize: '10px', color: '#52525B', marginTop: '4px', fontWeight: '700' }}>적용 불가</div>
                  )}
                </button>
              );
            })}
          </div>

          {comparison.recommendation.strong && revenue > 0 && (
            <div style={{ background: '#FAFAFA', borderRadius: '10px', padding: '12px 14px', borderLeft: '4px solid #FF6F0F' }}>
              <div style={{ fontSize: '12px', color: '#18181B', fontWeight: '800' }}>
                🎯 추천: <strong>{comparison.recommendation.label}</strong> 으로 신고 시 {comparison.recommendation.runnerUp} 대비 소득 {fmtWon(comparison.recommendation.savings)} 적게 잡혀요
              </div>
            </div>
          )}
          {revenue > 0 && !comparison.recommendation.strong && (
            <div style={{ background: '#FAFAFA', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', color: '#52525B' }}>
              💡 모드별 차이가 100만원 이내라 어떤 방식이든 비슷합니다. 영수증 증빙 부담을 고려해서 선택하세요.
            </div>
          )}

          {/* 간편장부 + 음식점업이면 단순경비율 비교 hint */}
          {finalMode === 'simpleBook' && comparison.simple.income < comparison.simpleBook.income && (
            <div style={{ marginTop: '8px', background: '#fef2f2', borderRadius: '10px', padding: '11px 14px', fontSize: '12px', color: '#52525B' }}>
              ⚠️ 음식점업 단순경비율(89.7%)은 후한 편입니다. 입력하신 실비({fmtWon(totalExpense)})가 매출의 {revenue > 0 ? ((totalExpense/revenue)*100).toFixed(1) : 0}%라 단순경비율 추계가 더 유리해요. 영수증 빠진 게 있는지 확인해보세요.
            </div>
          )}
        </div>
      </Card>

      {/* 4. 공제·감면 */}
      <Card>
        <CardHeader icon="💳" title="공제 · 감면" color="#7C3AED" />
        <div style={{ padding: '16px 18px' }}>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '8px' }}>소득공제</div>
            {[
              { key: 'personal', label: '기본공제 (본인)', hint: '본인 150만원' },
              { key: 'pension', label: '노란우산공제', hint: '한도 200~500만원' },
              { key: 'insurance', label: '국민연금·건강보험', hint: '납입액 전액' },
              { key: 'other', label: '기타 (의료·교육 등)', hint: '한도 별도' },
            ].map(item => (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '7px' }}>
                <span style={{ fontSize: '13px', color: '#555', width: '160px', flexShrink: 0 }}>{item.label}</span>
                <input type="number" value={deductions[item.key] || ''} placeholder="0"
                  onChange={e => setDeductions({...deductions, [item.key]: Number(e.target.value)||0})}
                  style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', textAlign: 'right' }} />
                <span style={{ fontSize: '11px', color: '#9ca3af', width: '120px', flexShrink: 0 }}>{item.hint}</span>
              </div>
            ))}
          </div>

          {/* 청년창업감면 */}
          <div style={{ background: '#faf5ff', borderRadius: '10px', padding: '12px 14px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: youthCredit.enabled ? '10px' : 0 }}>
              <input type="checkbox" checked={youthCredit.enabled} onChange={e => setYouthCredit({...youthCredit, enabled: e.target.checked})} style={{ width: '16px', height: '16px' }} />
              <span style={{ fontSize: '13px', fontWeight: '800', color: '#7C3AED' }}>🌱 청년창업중소기업 세액감면 (조특법 §6)</span>
            </label>
            {youthCredit.enabled && (
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
                  만 15~34세, 음식점업 포함 지정업종, 5년간 산출세액 감면
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  {[
                    { id: 'non_capital', label: '수도권 외 / 인구감소지역', sub: youthCredit.startedYear < 2026 ? '100%' : '100%' },
                    { id: 'capital_general', label: '수도권 (과밀억제 외)', sub: youthCredit.startedYear < 2026 ? '100%' : '75%' },
                    { id: 'capital_overcrowded', label: '수도권 과밀억제권역', sub: '50%' },
                  ].map(r => (
                    <button key={r.id} onClick={() => setYouthCredit({...youthCredit, region: r.id})}
                      style={{
                        flex: 1, minWidth: '120px', padding: '9px', borderRadius: '8px', cursor: 'pointer',
                        border: youthCredit.region === r.id ? '2px solid #7C3AED' : '2px solid #e5e7eb',
                        background: youthCredit.region === r.id ? '#EDE9FE' : '#fff',
                        textAlign: 'left',
                      }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#374151' }}>{r.label}</div>
                      <div style={{ fontSize: '14px', fontWeight: '900', color: '#7C3AED', marginTop: '2px' }}>{r.sub}</div>
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>창업연도</span>
                  <input type="number" value={youthCredit.startedYear} onChange={e => setYouthCredit({...youthCredit, startedYear: Number(e.target.value)})}
                    style={{ width: '100px', padding: '6px 10px', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>2026년 1월 이후 창업분부터 수도권 일반 감면율 75%로 변경</span>
                </div>
              </div>
            )}
          </div>

          {/* 기장세액공제 (간편장부 + 복식부기 신고) */}
          {finalMode === 'simpleBook' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: '#EFF6FF', borderRadius: '10px', padding: '11px 14px' }}>
              <input type="checkbox" checked={bookkeepingCredit} onChange={e => setBookkeepingCredit(e.target.checked)} style={{ width: '16px', height: '16px' }} />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '800', color: '#1D4ED8' }}>📒 기장세액공제 20% (한도 100만원)</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>간편장부 대상자가 복식부기로 작성하여 신고할 때만 적용</div>
              </div>
            </label>
          )}
        </div>
      </Card>

      {/* 5. 최종 세액 */}
      <Card>
        <CardHeader icon="🧮" title="예상 세액 계산" color="#52525B"
          badge={{ text: '5월 신고', bg: '#F4F4F5', color: '#52525B' }} />
        <div style={{ padding: '16px 18px' }}>
          <div style={{ background: '#FFFBEB', borderRadius: '12px', padding: '14px 16px' }}>
            <InfoRow label={`사업소득금액 (${pickedMode === 'auto' ? `자동 — ${comparison.recommendation.label}` : (finalMode === 'simple' ? '단순경비율' : finalMode === 'standard' ? '기준경비율' : '간편장부')})`} value={fmtWon(finalIncome)} />
            <InfoRow label="(-) 소득공제 합계" value={`-${fmtWon(totalDeductions)}`} />
            <InfoRow label="과세표준" value={fmtWon(taxableIncome)} />
            <InfoRow label="적용세율" value={`${bracket ? (bracket.rate * 100).toFixed(0) : 0}%`} />
            <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 0' }} />
            <InfoRow label="산출세액" value={fmtWon(baseTax)} />
            {youthApplied.credit > 0 && <InfoRow label={`(-) 청년창업감면 (${(youthApplied.rate*100).toFixed(0)}%)`} value={`-${fmtWon(youthApplied.credit)}`} valueColor="#7C3AED" />}
            {bkCredit.credit > 0 && <InfoRow label="(-) 기장세액공제 20%" value={`-${fmtWon(bkCredit.credit)}`} valueColor="#1D4ED8" />}
            <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 0' }} />
            <InfoRow label="결정세액 (소득세)" value={fmtWon(incomeTaxAfterCredits)} />
            <InfoRow label="지방소득세 (10%)" value={fmtWon(localTax)} />
            <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 0' }} />
            <InfoRow label="🔴 예상 납부세액" value={fmtWon(totalTax)} valueColor="#52525B" bold />
          </div>

          <div style={{ marginTop: '10px', background: '#f9fafb', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#6b7280' }}>
            ⚠️ <strong>참고용 추정치</strong>입니다. 경비율은 매년 국세청 고시로 변경되고, 실제 신고는 홈택스/세무사 확인 필요.
          </div>

          <div style={{ marginTop: '14px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <HometaxButton guide={HOMETAX_GUIDES.income} />
          </div>
          <StepGuide guide={HOMETAX_GUIDES.income} />
        </div>
      </Card>
    </div>
  );
}

// ── 탭: 원천세
function WithholdingTab({ taxData, onTaxDataChange, employees: payrollEmps = [], schedules = {}, payrollSettings = {} }) {
  const employees = taxData?.employees || [];
  const [newEmp, setNewEmp] = useState({ name: '', salary: '', type: 'employee' });
  const [showAdd, setShowAdd] = useState(false);

  const addEmployee = () => {
    if (!newEmp.name || !newEmp.salary) return;
    const emp = { id: String(Date.now()), ...newEmp, salary: Number(newEmp.salary) };
    onTaxDataChange({ ...taxData, employees: [...employees, emp] });
    setNewEmp({ name: '', salary: '', type: 'employee' });
    setShowAdd(false);
  };

  const removeEmployee = (id) => {
    onTaxDataChange({ ...taxData, employees: employees.filter(e => e.id !== id) });
  };

  // 인건비 모듈 → 원천세 탭으로 직원 가져오기 (이번 달 급여 기준)
  const importFromPayroll = () => {
    if (!Array.isArray(payrollEmps) || payrollEmps.length === 0) {
      alert('인건비 모듈에 등록된 직원이 없어요.\n인건비 탭 → 직원 → 직원 추가 후 다시 시도하세요.');
      return;
    }
    const today = new Date();
    const monthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const monthData = schedules?.[monthKey] || {};
    const active = payrollEmps.filter(e => e.active !== false);

    const imported = active.map(emp => {
      const monthShifts = [];
      for (let d = 1; d <= 31; d++) {
        const sh = monthData?.[d]?.[emp.id];
        if (sh) monthShifts.push({ date: `${monthKey}-${String(d).padStart(2, '0')}`, ...sh });
      }
      const payroll = calcMonthlyPayroll(emp, monthShifts, payrollSettings);
      const salary = (payroll?.totalEarnings) || (Number(emp.monthlyWage) || 0) || 0;
      // 알바·사업소득자 → freelance, 정직원 → employee
      const type = (emp.type === 'freelancer' || emp.type === 'parttime') ? 'freelance' : 'employee';
      return {
        id: `payroll-${emp.id}`,
        name: emp.name,
        salary,
        type,
      };
    }).filter(e => e.salary > 0);

    if (imported.length === 0) {
      alert('이번 달 스케줄에 근무 기록이 있는 직원이 없어요.\n인건비 → 입력에서 스케줄을 입력하면 급여가 자동 계산됩니다.');
      return;
    }

    if (!window.confirm(
      `이번 달(${monthKey}) 인건비 모듈에서 ${imported.length}명을 가져옵니다.\n\n` +
      imported.map(e => `• ${e.name} (${e.type === 'freelance' ? '3.3%' : '직원'}): ${e.salary.toLocaleString()}원`).join('\n') +
      `\n\n같은 ID의 기존 항목은 덮어씌웁니다. 계속할까요?`
    )) return;

    // 기존 payroll-* 항목은 덮어쓰기, 그 외는 유지
    const others = employees.filter(e => !String(e.id).startsWith('payroll-'));
    onTaxDataChange({ ...taxData, employees: [...others, ...imported] });
    alert(`✅ ${imported.length}명 가져오기 완료`);
  };

  const totalSalary = employees.reduce((s, e) => s + (e.salary || 0), 0);
  const totalWithholding = employees.reduce((s, e) => {
    const rate = e.type === 'freelance' ? WITHHOLDING_RATE : EMPLOYEE_RATE;
    return s + Math.round((e.salary || 0) * rate);
  }, 0);

  return (
    <div>
      <Card>
        <CardHeader icon="👥" title="직원·프리랜서 관리" color="#7C3AED" />
        <div style={{ padding: '16px 18px' }}>
          {/* 인건비 모듈에서 가져오기 */}
          {payrollEmps.length > 0 && (
            <div style={{
              background: '#faf5ff', border: '1.5px solid #c4b5fd',
              borderRadius: 10, padding: '10px 14px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 18 }}>💼</span>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#6b21a8' }}>
                  인건비 모듈 자동 연동
                </div>
                <div style={{ fontSize: 11, color: '#7e22ce', marginTop: 2 }}>
                  이번 달 직원 {payrollEmps.filter(e => e.active !== false).length}명의 급여를 한 번에 가져옵니다
                </div>
              </div>
              <button onClick={importFromPayroll}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none',
                  background: '#7C3AED', color: '#fff', fontWeight: 800, fontSize: 12,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}>📥 가져오기</button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '140px', background: '#FAFAFA', borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '3px' }}>총 인건비</div>
              <div style={{ fontSize: '18px', fontWeight: '900', color: '#18181B' }}>{fmtWon(totalSalary)}</div>
            </div>
            <div style={{ flex: 1, minWidth: '140px', background: '#faf5ff', borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '3px' }}>월 원천징수 합계</div>
              <div style={{ fontSize: '18px', fontWeight: '900', color: '#7C3AED' }}>{fmtWon(totalWithholding)}</div>
            </div>
          </div>

          {employees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#bbb', fontSize: '13px' }}>
              직원이나 프리랜서를 추가해보세요
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {employees.map(emp => {
                const rate = emp.type === 'freelance' ? WITHHOLDING_RATE : EMPLOYEE_RATE;
                const withholding = Math.round(emp.salary * rate);
                return (
                  <div key={emp.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 14px', background: '#f9fafb', borderRadius: '10px',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px' }}>{emp.name}</span>
                        <span style={{
                          fontSize: '10px', padding: '1px 7px', borderRadius: '8px', fontWeight: '700',
                          background: emp.type === 'freelance' ? '#F4F4F5' : '#EFF6FF',
                          color: emp.type === 'freelance' ? '#52525B' : '#1D4ED8',
                        }}>{emp.type === 'freelance' ? '프리랜서 3.3%' : '직원 9.4%'}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        급여 {fmtWon(emp.salary)} → 원천징수 {fmtWon(withholding)} → 실수령 {fmtWon(emp.salary - withholding)}
                      </div>
                    </div>
                    <button onClick={() => removeEmployee(emp.id)}
                      style={{ background: 'none', border: 'none', color: '#52525B', cursor: 'pointer', fontSize: '16px', padding: '4px' }}>
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {showAdd ? (
            <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input value={newEmp.name} onChange={e => setNewEmp({...newEmp, name: e.target.value})}
                  placeholder="이름" style={{ flex: 1, minWidth: '100px', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px' }} />
                <input type="number" value={newEmp.salary || ''} onChange={e => setNewEmp({...newEmp, salary: e.target.value})}
                  placeholder="월 급여 (원)" style={{ flex: 1, minWidth: '120px', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', textAlign: 'right' }} />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {['employee', 'freelance'].map(t => (
                  <button key={t} onClick={() => setNewEmp({...newEmp, type: t})}
                    style={{
                      flex: 1, padding: '8px', borderRadius: '8px', cursor: 'pointer',
                      border: newEmp.type === t ? '2px solid #7C3AED' : '2px solid #e5e7eb',
                      background: newEmp.type === t ? '#EDE9FE' : '#fff',
                      color: newEmp.type === t ? '#7C3AED' : '#555', fontSize: '12px', fontWeight: '700',
                    }}>{t === 'employee' ? '직원 (9.4%)' : '프리랜서 (3.3%)'}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={addEmployee} style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', background: '#7C3AED', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>추가</button>
                <button onClick={() => setShowAdd(false)} style={{ padding: '9px 16px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>취소</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)} style={{
              width: '100%', padding: '10px', borderRadius: '10px',
              border: '2px dashed #e5e7eb', background: '#fff',
              color: '#7C3AED', fontWeight: '700', fontSize: '13px', cursor: 'pointer',
            }}>+ 직원 / 프리랜서 추가</button>
          )}

          {totalWithholding > 0 && (
            <div style={{ marginTop: '14px', background: '#faf5ff', borderRadius: '12px', padding: '12px 14px' }}>
              <div style={{ fontSize: '12px', color: '#7C3AED', fontWeight: '700', marginBottom: '6px' }}>📅 매월 10일까지 납부</div>
              <div style={{ fontSize: '13px', color: '#374151' }}>
                원천징수세액 {fmtWon(totalWithholding)} → 소득세 {fmtWon(Math.round(totalWithholding/1.1))} + 지방소득세 {fmtWon(Math.round(totalWithholding/11))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '14px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <HometaxButton guide={HOMETAX_GUIDES.withholding} />
          </div>
          <StepGuide guide={HOMETAX_GUIDES.withholding} />
        </div>
      </Card>
    </div>
  );
}

// ── 탭: 세금계산서
function TaxInvoiceTab({ taxData, onTaxDataChange }) {
  const invoices = taxData?.invoices || [];
  const [form, setForm] = useState({ date: '', amount: '', vendor: '', type: 'received', memo: '' });
  const [showAdd, setShowAdd] = useState(false);

  const addInvoice = () => {
    if (!form.date || !form.amount) return;
    const inv = { id: String(Date.now()), ...form, amount: Number(form.amount) };
    onTaxDataChange({ ...taxData, invoices: [inv, ...invoices] });
    setForm({ date: '', amount: '', vendor: '', type: 'received', memo: '' });
    setShowAdd(false);
  };

  const removeInvoice = (id) => onTaxDataChange({ ...taxData, invoices: invoices.filter(i => i.id !== id) });

  const received = invoices.filter(i => i.type === 'received');
  const issued = invoices.filter(i => i.type === 'issued');
  const receivedTotal = received.reduce((s, i) => s + i.amount, 0);
  const issuedTotal = issued.reduce((s, i) => s + i.amount, 0);
  const receivedVat = Math.round(receivedTotal * 0.1);
  const issuedVat = Math.round(issuedTotal * 0.1);

  return (
    <div>
      <Card>
        <CardHeader icon="🧾" title="세금계산서 관리" color="#374151" />
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '130px', background: '#EFF6FF', borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>수취 (매입) 총액</div>
              <div style={{ fontSize: '16px', fontWeight: '900', color: '#1A56A0' }}>{fmtWon(receivedTotal)}</div>
              <div style={{ fontSize: '11px', color: '#1A56A0', marginTop: '2px' }}>공제세액 {fmtWon(receivedVat)}</div>
            </div>
            <div style={{ flex: 1, minWidth: '130px', background: '#FAFAFA', borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>발행 (매출) 총액</div>
              <div style={{ fontSize: '16px', fontWeight: '900', color: '#18181B' }}>{fmtWon(issuedTotal)}</div>
              <div style={{ fontSize: '11px', color: '#18181B', marginTop: '2px' }}>납부세액 {fmtWon(issuedVat)}</div>
            </div>
          </div>

          {showAdd ? (
            <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {['received', 'issued'].map(t => (
                  <button key={t} onClick={() => setForm({...form, type: t})}
                    style={{
                      flex: 1, padding: '8px', borderRadius: '8px', cursor: 'pointer',
                      border: form.type === t ? '2px solid #1A56A0' : '2px solid #e5e7eb',
                      background: form.type === t ? '#EFF6FF' : '#fff',
                      color: form.type === t ? '#1A56A0' : '#555', fontSize: '13px', fontWeight: '700',
                    }}>{t === 'received' ? '📥 수취 (매입)' : '📤 발행 (매출)'}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                  style={{ flex: 1, minWidth: '130px', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px' }} />
                <input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})}
                  placeholder="거래처명" style={{ flex: 1, minWidth: '100px', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px' }} />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input type="number" value={form.amount || ''} onChange={e => setForm({...form, amount: e.target.value})}
                  placeholder="공급가액 (부가세 제외)" style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', textAlign: 'right' }} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={addInvoice} style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', background: '#374151', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>저장</button>
                <button onClick={() => setShowAdd(false)} style={{ padding: '9px 16px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>취소</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)} style={{
              width: '100%', padding: '10px', borderRadius: '10px', marginBottom: '12px',
              border: '2px dashed #e5e7eb', background: '#fff',
              color: '#374151', fontWeight: '700', fontSize: '13px', cursor: 'pointer',
            }}>+ 세금계산서 추가</button>
          )}

          {invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px', color: '#bbb', fontSize: '13px' }}>
              세금계산서를 추가하면 부가세 신고에 자동 반영돼요
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {invoices.slice(0, 20).map(inv => (
                <div key={inv.id} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px', background: '#f9fafb', borderRadius: '8px',
                }}>
                  <span style={{ fontSize: '16px' }}>{inv.type === 'received' ? '📥' : '📤'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a' }}>{inv.vendor || '거래처 없음'}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>{inv.date} · {inv.type === 'received' ? '수취' : '발행'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: inv.type === 'received' ? '#1A56A0' : '#FF6F0F' }}>
                      {fmtWon(inv.amount)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#9ca3af' }}>부가세 {fmtWon(Math.round(inv.amount*0.1))}</div>
                  </div>
                  <button onClick={() => removeInvoice(inv.id)}
                    style={{ background: 'none', border: 'none', color: '#52525B', cursor: 'pointer', fontSize: '14px', padding: '2px' }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ── 메인 컴포넌트
const TABS = [
  { id: 'ledger',      label: '거래장부', icon: '📒' },
  { id: 'deadline',    label: '신고 일정', icon: '📅' },
  { id: 'vat',         label: '부가세',    icon: '💰' },
  { id: 'income',      label: '종합소득세', icon: '📊' },
  { id: 'withholding', label: '원천세',    icon: '👥' },
  { id: 'invoice',     label: '세금계산서', icon: '🧾' },
];

export default function TaxCenter({
  profitLoss, taxData, onTaxDataChange, canAccess, user, prices,
  employees = [], schedules = {}, payrollSettings = {},
}) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('ledger');

  if (!canAccess('pro') && !isAdmin(user)) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '14px', padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '52px' }}>🏛️</div>
        <h2 style={{ fontSize: '20px', fontWeight: '800' }}>세무·회계 센터</h2>
        <p style={{ color: '#6b7280', lineHeight: 1.7, maxWidth: '320px' }}>
          <strong>프로 플랜</strong> 전용 기능이에요.<br />
          부가세·종합소득세·원천세·세금계산서를 한 곳에서 관리하세요.
        </p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
      {/* 탭 헤더 */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e5e7eb',
        display: 'flex', overflowX: 'auto',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: isMobile ? '12px 14px' : '14px 20px',
            border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: '700', fontSize: isMobile ? '12px' : '13px',
            color: tab === t.id ? '#FF6F0F' : '#9ca3af',
            borderBottom: tab === t.id ? '2px solid #FF6F0F' : '2px solid transparent',
            whiteSpace: 'nowrap', flexShrink: 0,
            transition: 'all 0.15s',
          }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: isMobile ? '12px' : '20px' }}>
        {tab === 'ledger'      && <LedgerTab user={user} prices={prices} profitLoss={profitLoss} />}
        {tab === 'deadline'    && <DeadlineTab />}
        {tab === 'vat'         && <VatTab profitLoss={profitLoss} taxData={taxData} onTaxDataChange={onTaxDataChange} user={user} />}
        {tab === 'income'      && <IncomeTaxTab profitLoss={profitLoss} />}
        {tab === 'withholding' && <WithholdingTab taxData={taxData} onTaxDataChange={onTaxDataChange} employees={employees} schedules={schedules} payrollSettings={payrollSettings} />}
        {tab === 'invoice'     && <TaxInvoiceTab taxData={taxData} onTaxDataChange={onTaxDataChange} />}
      </div>
    </div>
  );
}
